import React, { useEffect, useState } from "react";
import { api, rupee } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck, Store, Users, Wallet, Building2, ToggleRight, ToggleLeft } from "lucide-react";
import { toast } from "sonner";
import BrandLogo from "../components/BrandLogo";

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState([]);

  const load = () => {
    api.get("/admin/overview").then((r) => setOverview(r.data));
    api.get("/admin/tenants").then((r) => setTenants(r.data));
  };
  useEffect(() => { load(); }, []);

  const toggle = async (tid) => {
    await api.post(`/admin/tenants/${tid}/toggle`);
    toast.success("Updated");
    load();
  };

  return (
    <div className="min-h-screen grain-bg" data-testid="superadmin-root">
      <header className="border-b bg-white" style={{ borderColor: "var(--border-soft)" }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo to={null} size="sm" />
            <div>
              <div className="font-display font-bold text-lg">Super Admin</div>
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>{user?.email}</div>
            </div>
          </div>
          <button data-testid="superadmin-logout" onClick={() => { logout(); nav("/"); }} className="inline-flex items-center gap-2 h-11 px-4 rounded-md border" style={{ borderColor: "var(--border-soft)" }}>
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="font-display font-bold text-3xl mb-6">Platform overview</h1>
        {overview && (
          <div className="grid md:grid-cols-5 gap-4 mb-8">
            <StatC label="Tenants" v={overview.tenants} icon={Building2} color="var(--primary)" tid="sa-stat-tenants" />
            <StatC label="Users" v={overview.users} icon={Users} color="var(--secondary)" tid="sa-stat-users" />
            <StatC label="Active subs" v={overview.active_subscriptions} icon={ShieldCheck} color="var(--success)" tid="sa-stat-active" />
            <StatC label="Trial subs" v={overview.trial_subscriptions} icon={Store} color="var(--warning)" tid="sa-stat-trial" />
            <StatC label="Total GMV" v={rupee(overview.total_gmv)} icon={Wallet} color="var(--primary)" tid="sa-stat-gmv" />
          </div>
        )}

        <div className="card-soft">
          <div className="p-4 font-display font-semibold border-b" style={{ borderColor: "var(--border-soft)" }}>All tenants</div>
          <table className="w-full text-sm zebra">
            <thead><tr className="text-left" style={{ color: "var(--text-muted)" }}><th className="p-3">Store</th><th>Plan</th><th>Status</th><th className="text-right">Products</th><th className="text-right">Sales</th><th className="text-right">Revenue</th><th className="text-right">Users</th><th></th></tr></thead>
            <tbody>
              {tenants.length === 0 && <tr><td colSpan={8} className="text-center py-10" style={{ color: "var(--text-muted)" }}>No tenants yet.</td></tr>}
              {tenants.map((t) => (
                <tr key={t.id} className="border-t" style={{ borderColor: "var(--border-soft)" }} data-testid={`sa-tenant-${t.id}`}>
                  <td className="p-3">
                    <div className="font-medium">{t.store_name}</div>
                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t.owner_email || t.phone || "—"}</div>
                  </td>
                  <td className="capitalize">{t.subscription?.status === "trial" ? "—" : (t.subscription?.plan || "—")}</td>
                  <td><span className="text-xs px-2 py-1 rounded-full" style={{ background: t.subscription?.status === "active" ? "var(--surface-success)" : "var(--surface-alert)", color: t.subscription?.status === "active" ? "var(--success)" : "var(--warning)" }}>{t.subscription?.status}</span></td>
                  <td className="text-right">{t.stats.products}</td>
                  <td className="text-right">{t.stats.sales}</td>
                  <td className="text-right num font-semibold">{rupee(t.stats.revenue)}</td>
                  <td className="text-right">{t.stats.users}</td>
                  <td className="text-right pr-3">
                    <button data-testid={`sa-toggle-${t.id}`} onClick={() => toggle(t.id)} className="inline-flex items-center gap-1 h-9 px-3 rounded-md border" style={{ borderColor: "var(--border-soft)" }}>
                      {t.active ? <><ToggleRight size={16} style={{ color: "var(--success)" }} /> Active</> : <><ToggleLeft size={16} style={{ color: "var(--error)" }} /> Disabled</>}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatC({ label, v, icon: I, color, tid }) {
  return (
    <div className="card-soft p-5" data-testid={tid}>
      <div className="flex items-center justify-between">
        <div className="text-sm" style={{ color: "var(--text-muted)" }}>{label}</div>
        <I size={18} style={{ color }} />
      </div>
      <div className="font-display font-bold text-2xl mt-2 num">{v}</div>
    </div>
  );
}
